<template>
  <general-loader v-if="loading" loading/>
  <TabLayout v-else class="customer-view" :tabs="tabs">
    <template v-slot:title>
      <artisio-breadcrumb :items="breadcrumbs"/>
      <span class="ml-3" v-as-status="customer.status">
        {{ customer.status | removeUnderscores | toUpperCaseFirst | translate }}
      </span>
    </template>
    <template v-slot:buttons>
      <b-button v-permission="'change_customer'" variant="primary" @click="showModal" class="mr-2">
        <font-awesome-icon icon="plus-circle" class="icon"/>
        {{ $t('Create Customer') }}
      </b-button>
      <b-button v-permission="'change_receipt'" variant="primary" @click="onReceiptCreateClick" class="mr-2">
        <font-awesome-icon icon="plus-circle" class="icon"/>
        {{ $t('Create Receipt') }}
      </b-button>
      <b-dropdown right id="dropdown-1" :text="$t('More')" class="pull-right" v-permission="'change_customer'">
        <b-dropdown-item v-permission="'change_customer'"
                         @click="onEditClick(customer)">
          {{ $t('Edit Customer') }}
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item v-permission="'change_customer'" @click="changeStatus('active')">
          {{ $t('Mark As Active') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_customer'" @click="changeStatus('pending')">
          {{ $t('Mark As Pending') }}
        </b-dropdown-item>
        <b-dropdown-item v-permission="'change_customer'" @click="changeStatus('blocked')">
          {{ $t('Mark As Blocked') }}
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item v-permission="'delete_customer'" class="text-danger" @click="onCustomerDeleteClick">
                <span class="text-danger">
                  {{ $t('Delete Customer') }}
                </span>
        </b-dropdown-item>
      </b-dropdown>
    </template>
    <template v-slot:right-placeholder>
      <b-form-select v-if="isDashboard" @change="onDateChange" v-model="selectedYear"
                     :options="yearOptions"></b-form-select>
    </template>
  </TabLayout>
</template>

<script>
  import {createNamespacedHelpers} from 'vuex';
  import CustomerService from "./../CustomerService";
  import {SOURCE_CUSTOMER_DASHBOARD, SOURCE_CUSTOMER_VIEW} from "../../constants";
  import ArtisioBreadcrumb from "../../../core/components/artisio-breadcrumb/ArtisioBreadcrumb";
  import TabLayout from "../../../core/components/layout/DefaultLayout/TabLayout";
  import i18n from "../../../shared/i18n";
  import {ON_CUSTOMER_DASHBOARD_DATE_CHANGE} from "@/plugins/event-bus/event-bus-names";
  import GeneralLoader from "@/core/components/general-loader/GeneralLoader";
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('customer/customerView');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapActions: receiptMapActions} = createNamespacedHelpers('receipts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "CustomerView",
    components: {
      GeneralLoader,
      TabLayout, ArtisioBreadcrumb
    },
    data() {
      return {
        SOURCE_CUSTOMER_VIEW: SOURCE_CUSTOMER_VIEW,
        breadcrumbs: [],
        selectedYear: 1,
        yearOptions: [
          {value: '1', text: i18n.t('1 Year')},
          {value: '2', text: i18n.t('2 Years')},
          {value: '3', text: i18n.t('3 Years')},
          {value: '4', text: i18n.t('4 Years')},
          {value: '5', text: i18n.t('5 Years')},
        ],
      }
    },
    computed: {
      ...mapState({
        customer: state => state.customer,
        loading: state => state.loading,
      }),
      ...globalMapState({
        enableNlRelations: state => state.enableNlRelations,
      }),
      isDashboard() {
        return this.$route.name === 'customers.dashboard';
      },
      tabs() {
        let data = [
          {title: this.$i18n.t('Dashboard'), icon: 'server', to: {name: 'customers.dashboard'}},
          {title: this.$i18n.t('Addresses'), icon: 'address-book', to: {name: 'customers.addresses'}, viewPermission: 'view_customeraddress'},
          {title: this.$i18n.t('Receipts'), icon: 'receipt', to: {name: 'customers.receipts'}, viewPermission: 'view_receipt'},
          {title: this.$i18n.t('Bids'), icon: 'gavel', to: {name: 'customers.bids'}, viewPermission: 'view_auction'},
          {title: this.$i18n.t('Purchased Lots'), icon: 'boxes', to: {name: 'customers.purchasedLots'}},
          {title: this.$i18n.t('Watchlist'), icon: 'heart', to: {name: 'customers.favoriteLots'}},
          {title: this.$i18n.t('Terms'), icon: 'percentage', to: {name: 'customers.terms'}},
          {title: this.$i18n.t('Vendor Charges'), icon: 'percentage', to: {name: 'customers.vendorCharges'}},
          {title: this.$i18n.t('Relations'), icon: 'users', children: [
                {
                  title: this.$i18n.t('Relations'),
                  icon: 'users',
                  to: {name: 'customers.relations'},
                },
                {
                  title: this.$i18n.t('Related to'),
                  icon: 'users',
                  to: {name: 'customers.related_to'},
                }
              ]
          },
          {
            title: this.$i18n.t('Finance'), icon: 'file-invoice', children: [
              {
                title: this.$i18n.t('Invoices'),
                icon: 'file-invoice',
                to: {name: 'customers.invoices'},
                viewPermission: 'view_invoice'
              },
              {
                title: this.$i18n.t('Settlements'),
                icon: 'file-settlement',
                to: {name: 'customers.settlements'},
                viewPermission: 'view_settlement'
              },
              {
                title: this.$i18n.t('Deposits'),
                icon: 'file-invoice',
                to: {name: 'customers.deposits'},
                viewPermission: 'view_invoice'
              },
            ]
          },
          {title: this.$i18n.t('Optin'), icon: 'envelope', to: {name: 'customers.optin'}},
          {title: this.$i18n.t('Attachments'), icon: 'copy', to: {name: 'customers.attachments'}},
          {title: this.$i18n.t('Contracts'), icon: 'copy', to: {name: 'customers.contracts'}},
          {title: this.$i18n.t('Notes'), icon: 'comment-alt', to: {name: 'customers.notes'}},
          {title: this.$i18n.t('Activities'), icon: 'stream', to: {name: 'customers.activities'}},
        ]

        return data
      }
    },
    watch: {
      async '$route.params.id'() {
        await this.getCustomer(this.$route.params.id);
        this.initBreadcrumbs();
      },
      'customer.display_name': {
        deep: true,
        handler() {
          this.initBreadcrumbs();
        }
      }
    },
    methods: {
      ...mapActions(['getCustomer', 'savePartialData', 'updateViewCustomer']),
      ...receiptMapActions({
        showReceiptModal: 'showModal'
      }),
      ...customerMapActions(['showModal','getModalObject']),
      async changeStatus(status) {
        let success = await this.$confirm(
            this.$i18n.t(`Are you sure you want to mark customer as {status}?`, {status}),
            this.$i18n.t('Customer has been successfully updated'),
            CustomerService.partialUpdateCustomer,
            {id: this.customer.uuid, data: {status}}
        );
        if (success) {
          this.updateViewCustomer({status});
        }
      },
      async onCustomerDeleteClick() {
        let success = await this.$confirm(
            this.$i18n.t('Are you sure you want to delete following customer?'),
            this.$i18n.t('Customer has been successfully deleted'),
            CustomerService.removeCustomer,
            this.customer.uuid
        );
        if (success) {
          this.$router.push({name: 'customers'})
        }
      },
      onReceiptCreateClick() {
        this.showReceiptModal({
          object: {
            customer: this.customer,
            customer_uuid: this.customer.uuid
          }
        });
      },
      initBreadcrumbs() {
        this.breadcrumbs = [
          {
            text: this.$i18n.t('Customers'),
            to: {name: 'customers'},
            icon: 'user-tie'
          },
          {
            text: `${this.customer.customer_no} - ${this.customer.display_name}`,
          }
        ];
      },
      onDateChange() {
        this.$evBus.$emit(ON_CUSTOMER_DASHBOARD_DATE_CHANGE, this.selectedYear);
      },
      onEditClick(item){
        this.showModal();
        this.getModalObject({uuid: item.uuid, source: SOURCE_CUSTOMER_DASHBOARD});
      }
    },
    async beforeMount() {
      const response = await this.getCustomer(this.$route.params.id);
      if (!response.success) {
        if (response.response.status === 404) {
          await this.$errorDialog(this.$i18n.t('Customer not found'));
        } else {
          await this.$errorDialog(this.$i18n.t('Something went wrong'));
        }
        this.$generalGoBack(this)
      } else {
        this.initBreadcrumbs();
      }
    }
  }

</script>

<style lang="scss">
  @import "./../scss/variables";

  .customer-view {
    @include view-page-wrapper;
  }
</style>
